import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { 
  Building, Users, Link2, Clock, CheckCircle2, XCircle, Shield,
  Loader2, ChevronDown, ChevronUp, Ship, Check, X, Crown
} from 'lucide-react';
import AccountantAdminFeedback from './AccountantAdminFeedback';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AccountantAdmin = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCard, setExpandedCard] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/accountant/admin/stats`);
      setStats(res.data);
    } catch (err) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const toggle = (key) => setExpandedCard(prev => prev === key ? null : key);

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';

  const practices = useMemo(() => stats?.practices || [], [stats]);
  const approved = useMemo(() => practices.filter(p => p.status === 'approved'), [practices]);
  const pending = useMemo(() => practices.filter(p => p.status === 'pending'), [practices]);
  // Pre-sort copies to avoid mutating `approved` twice in render (Array.sort is in-place).
  const approvedByTeam = useMemo(
    () => [...approved].sort((a, b) => b.team_count - a.team_count),
    [approved]
  );
  const approvedByClients = useMemo(
    () => [...approved].sort((a, b) => b.client_count - a.client_count),
    [approved]
  );

  if (loading) {
    return <div className="flex items-center justify-center min-h-[50vh]"><Loader2 className="h-8 w-8 animate-spin text-sky-400" /></div>;
  }

  const s = stats?.summary || {};

  return (
    <div className="space-y-6 max-w-5xl" data-testid="accountant-admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Accountant Admin</h1>
          <p className="text-slate-400 text-sm mt-0.5">Portal statistics and practice management</p>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="flex gap-1 bg-slate-800 p-1 rounded-lg w-fit">
        {[
          { key: 'overview', label: 'Overview' },
          { key: 'feedback', label: 'Feedback' },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.key ? 'bg-sky-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-700'
            }`} data-testid={`acct-admin-tab-${tab.key}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (<>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <SummaryCard label="Total Practices" value={s.total_practices} icon={Building} color="text-sky-400"
          onClick={() => toggle('practices')} active={expandedCard === 'practices'} />
        <SummaryCard label="Accountant Users" value={s.total_accountant_users} icon={Users} color="text-violet-400"
          onClick={() => toggle('users')} active={expandedCard === 'users'} />
        <SummaryCard label="Linked Seafarers" value={s.total_confirmed_links} icon={Link2} color="text-emerald-400"
          onClick={() => toggle('links')} active={expandedCard === 'links'} />
        <SummaryCard label="Pending Links" value={s.total_pending_links} icon={Clock} color="text-amber-400" />
      </div>

      {/* Status Breakdown Row */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-3 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-400" />
            <div>
              <p className="text-xs text-slate-500">Approved</p>
              <p className="text-lg font-bold text-emerald-400">{s.approved_practices}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-3 flex items-center gap-2">
            <Clock className="h-4 w-4 text-amber-400" />
            <div>
              <p className="text-xs text-slate-500">Pending</p>
              <p className="text-lg font-bold text-amber-400">{s.pending_practices}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-3 flex items-center gap-2">
            <XCircle className="h-4 w-4 text-red-400" />
            <div>
              <p className="text-xs text-slate-500">Rejected</p>
              <p className="text-lg font-bold text-red-400">{s.rejected_practices}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Expanded: Practices */}
      {expandedCard === 'practices' && (
        <Card className="bg-slate-800 border-sky-500/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <Building className="h-4 w-4 text-sky-400" />
              All Practices ({practices.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-700">
              {practices.map(p => (
                <PracticeRow key={p.id} practice={p} formatDate={formatDate} onStatusChange={fetchStats} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Expanded: Users per practice */}
      {expandedCard === 'users' && (
        <Card className="bg-slate-800 border-violet-500/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <Users className="h-4 w-4 text-violet-400" />
              Licence Breakdown — Users per Practice
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-700">
              {approvedByTeam.map(p => (
                <div key={p.id} className="px-4 py-3 flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-sm text-white font-medium">{p.firm_name}</p>
                    <p className="text-xs text-slate-500">HMRC ARN: {p.hmrc_arn}</p>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-slate-400 shrink-0">
                    <div className="text-center">
                      <Users className="h-3.5 w-3.5 mx-auto mb-0.5 text-violet-400" />
                      <span className="font-medium text-white">{p.team_count}</span>
                      <span className="text-slate-500 ml-0.5">users</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Expanded: Client links per practice */}
      {expandedCard === 'links' && (
        <Card className="bg-slate-800 border-emerald-500/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <Ship className="h-4 w-4 text-emerald-400" />
              Seafarers per Practice
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-700">
              {approvedByClients.map(p => (
                <div key={p.id} className="px-4 py-3 flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-sm text-white font-medium">{p.firm_name}</p>
                    <p className="text-xs text-slate-500">Admin: {p.admin_name}</p>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-slate-400 shrink-0">
                    <div className="text-center">
                      <Ship className="h-3.5 w-3.5 mx-auto mb-0.5 text-emerald-400" />
                      <span className="font-medium text-white">{p.client_count}</span>
                      <span className="text-slate-500 ml-0.5">seafarers</span>
                    </div>
                    <div className="text-center">
                      <Users className="h-3.5 w-3.5 mx-auto mb-0.5 text-slate-500" />
                      <span>{p.team_count} staff</span>
                    </div>
                  </div>
                </div>
              ))}
              {approved.length === 0 && (
                <div className="p-6 text-center text-slate-500 text-sm">No approved practices yet.</div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      </>)}

      {/* Feedback Tab */}
      {activeTab === 'feedback' && (
        <AccountantAdminFeedback />
      )}
    </div>
  );
};

// Reusable summary card
const SummaryCard = ({ label, value, icon: Icon, color, onClick, active }) => (
  <Card className={`bg-slate-800 border-slate-700 transition-colors ${onClick ? 'cursor-pointer hover:border-slate-600' : ''} ${active ? 'border-sky-500/40' : ''}`}
    onClick={onClick}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">{label}</p>
        {onClick && (active ? <ChevronUp className="h-3.5 w-3.5 text-slate-400" /> : <ChevronDown className="h-3.5 w-3.5 text-slate-500" />)}
      </div>
      <div className="flex items-center gap-2 mt-1">
        <Icon className={`h-5 w-5 ${color}`} />
        <p className="text-2xl font-bold text-white">{value ?? 0}</p>
      </div>
    </CardContent>
  </Card>
);

// Practice row with management actions
const PracticeRow = ({ practice: p, formatDate, onStatusChange }) => {
  const [showSubEdit, setShowSubEdit] = useState(false);
  const [subTier, setSubTier] = useState(p.subscription_tier || 'free');
  const [maxTeam, setMaxTeam] = useState(p.max_team || 5);
  const [maxClients, setMaxClients] = useState(p.max_clients || 50);
  const [saving, setSaving] = useState(false);

  const statusColors = {
    approved: 'text-emerald-400 bg-emerald-500/10',
    pending: 'text-amber-400 bg-amber-500/10',
    rejected: 'text-red-400 bg-red-500/10',
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.put(`${API_URL}/api/accountant/admin/practices/${p.id}/status`, { status: newStatus });
      toast.success(`Practice ${newStatus}`);
      if (onStatusChange) onStatusChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleSubSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/accountant/admin/practices/${p.id}/subscription`, {
        subscription_tier: subTier,
        max_team: parseInt(maxTeam),
        max_clients: parseInt(maxClients),
      });
      toast.success('Subscription updated');
      setShowSubEdit(false);
      if (onStatusChange) onStatusChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const tierBadge = {
    free: <Badge className="bg-slate-500/20 text-slate-400 border-slate-500/30 text-[10px]">Free</Badge>,
    standard: <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-[10px]">Standard</Badge>,
    premium: <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-[10px]">Premium</Badge>,
  };

  return (
    <div className="px-4 py-3" data-testid={`practice-row-${p.id}`}>
      <div className="flex items-center justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-medium text-white">{p.firm_name}</p>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${statusColors[p.status] || 'text-slate-400 bg-slate-700'}`}>
              {p.status}
            </span>
            {tierBadge[p.subscription_tier || 'free']}
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5 flex-wrap">
            <span>ARN: {p.hmrc_arn}</span>
            <span>Admin: {p.admin_name}</span>
            <span>Since {formatDate(p.created_at)}</span>
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-400 shrink-0">
          <div className="text-center">
            <p className="text-white font-medium">{p.team_count}</p>
            <p className="text-slate-600">staff</p>
          </div>
          <div className="text-center">
            <p className="text-white font-medium">{p.client_count}</p>
            <p className="text-slate-600">clients</p>
          </div>
        </div>
      </div>
      {/* Actions */}
      <div className="flex items-center gap-2 mt-2 flex-wrap">
        {p.status === 'pending' && (
          <>
            <Button size="sm" onClick={() => handleStatusChange('approved')}
              className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs" data-testid={`approve-${p.id}`}>
              <Check className="h-3 w-3 mr-1" />Approve
            </Button>
            <Button size="sm" variant="ghost" onClick={() => handleStatusChange('rejected')}
              className="text-red-400 hover:bg-red-500/10 h-7 text-xs" data-testid={`reject-${p.id}`}>
              <X className="h-3 w-3 mr-1" />Reject
            </Button>
          </>
        )}
        {p.status === 'rejected' && (
          <Button size="sm" onClick={() => handleStatusChange('approved')}
            className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs" data-testid={`approve-${p.id}`}>
            <Check className="h-3 w-3 mr-1" />Approve
          </Button>
        )}
        {p.status === 'approved' && (
          <Button size="sm" variant="ghost" onClick={() => handleStatusChange('rejected')}
            className="text-red-400 hover:bg-red-500/10 h-7 text-xs" data-testid={`revoke-${p.id}`}>
            <X className="h-3 w-3 mr-1" />Revoke
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={() => setShowSubEdit(!showSubEdit)}
          className="text-sky-400 hover:bg-sky-500/10 h-7 text-xs" data-testid={`sub-edit-${p.id}`}>
          <Crown className="h-3 w-3 mr-1" />Subscription
        </Button>
      </div>
      {/* Subscription Editor */}
      {showSubEdit && (
        <div className="mt-2 p-3 bg-slate-700/30 rounded-lg border border-slate-600 space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="space-y-1">
              <label className="text-[10px] text-slate-400 uppercase">Tier</label>
              <select value={subTier} onChange={e => setSubTier(e.target.value)}
                className="bg-slate-800 border border-slate-600 text-white rounded-md h-8 text-xs px-2"
                data-testid={`sub-tier-${p.id}`}>
                <option value="free">Free</option>
                <option value="standard">Standard</option>
                <option value="premium">Premium</option>
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-[10px] text-slate-400 uppercase">Max Team</label>
              <Input type="number" min="1" max="100" value={maxTeam} onChange={e => setMaxTeam(e.target.value)}
                className="bg-slate-800 border-slate-600 text-white w-16 h-8 text-xs" data-testid={`sub-team-${p.id}`} />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] text-slate-400 uppercase">Max Clients</label>
              <Input type="number" min="1" max="1000" value={maxClients} onChange={e => setMaxClients(e.target.value)}
                className="bg-slate-800 border-slate-600 text-white w-20 h-8 text-xs" data-testid={`sub-clients-${p.id}`} />
            </div>
            <div className="flex items-end gap-2">
              <Button size="sm" onClick={handleSubSave} disabled={saving}
                className="bg-sky-600 hover:bg-sky-700 h-8 text-xs" data-testid={`sub-save-${p.id}`}>
                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowSubEdit(false)}
                className="h-8 text-xs text-slate-400">Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountantAdmin;
